import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { crmService } from '../../services/crmService';
import { useAuth } from '../../contexts/AuthContext';
import { useCRMActivities } from '../../hooks/useCRMActivities';
import AgendaView from './components/AgendaView';
import RecordatoriosView from './components/RecordatoriosView';
import WhatsAppModal from './components/WhatsAppModal';
import AgendarTrabajoModal from './components/AgendarTrabajoModal';
import SolicitudesPortalPanel from './components/SolicitudesPortalPanel';

// ─── Constants ────────────────────────────────────────────────────────────────
const ETAPAS = [
  { id: 'nuevo',          label: 'Nuevo',          color: 'bg-blue-500',   text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  { id: 'contactado',     label: 'Contactado',     color: 'bg-cyan-500',   text: 'text-cyan-700',   bg: 'bg-cyan-50',   border: 'border-cyan-200' },
  { id: 'cotizado',       label: 'Cotizado',       color: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  { id: 'seguimiento',    label: 'Seguimiento',    color: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
  { id: 'cerrado_ganado', label: 'Cerrado ganado', color: 'bg-green-500',  text: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200' },
  { id: 'cerrado_perdido',label: 'Cerrado perdido',color: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200' },
];

const ORIGENES = [
  { id: 'llamada_entrante', label: 'Llamada entrante' },
  { id: 'recomendacion',    label: 'Recomendación' },
  { id: 'redes_sociales',   label: 'Redes sociales' },
  { id: 'visita_directa',   label: 'Visita directa' },
  { id: 'otro',             label: 'Otro' },
];

const TIPOS_ACTIVIDAD = [
  { id: 'llamada',     label: 'Llamada',     icon: 'Phone' },
  { id: 'whatsapp',    label: 'WhatsApp',    icon: 'MessageCircle' },
  { id: 'mensaje',     label: 'Mensaje',     icon: 'MessageSquare' },
  { id: 'email',       label: 'Email',       icon: 'Mail' },
  { id: 'cita',        label: 'Cita',        icon: 'Calendar' },
  { id: 'seguimiento', label: 'Seguimiento', icon: 'RefreshCw' },
];

const ESTADOS_ACTIVIDAD = [
  { id: 'pendiente',  label: 'Pendiente' },
  { id: 'realizado',  label: 'Realizado' },
  { id: 'cancelado',  label: 'Cancelado' },
];

// ─── Helper Functions ─────────────────────────────────────────────────────────
const getEtapa = (etapaId) => ETAPAS?.find(e => e?.id === etapaId);
const getTipoActividad = (tipoId) => TIPOS_ACTIVIDAD?.find(t => t?.id === tipoId);

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    <Icon name={type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} />
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><Icon name="X" size={14} /></button>
  </div>
);

// ─── Etapa Badge ─────────────────────────────────────────────────────────────
const EtapaBadge = ({ etapa }) => {
  const e = getEtapa(etapa);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${e?.bg} ${e?.text} border ${e?.border}`}>
      {e?.label}
    </span>
  );
};

// ─── Nuevo Prospecto Modal ────────────────────────────────────────────────────
const NuevoProspectoModal = ({ onClose, onSave, companyId }) => {
  const [form, setForm] = useState({
    nombre: '', empresa: '', telefono: '', whatsapp: '', email: '',
    origen: '', servicio_interes: '', notas: '', responsable: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.nombre?.trim()) { setError('El nombre es requerido'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: companyId,
        etapa: 'nuevo',
        responsable: form?.responsable || null,
        client_id: form?.client_id || null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Nuevo prospecto</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Nombre <span className="text-red-500">*</span></label>
              <input value={form?.nombre} onChange={e => handleChange('nombre', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Nombre completo" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Empresa</label>
              <input value={form?.empresa} onChange={e => handleChange('empresa', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Empresa" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Teléfono</label>
              <input value={form?.telefono} onChange={e => handleChange('telefono', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Teléfono" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">WhatsApp</label>
              <input value={form?.whatsapp} onChange={e => handleChange('whatsapp', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="WhatsApp" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Email</label>
              <input type="email" value={form?.email} onChange={e => handleChange('email', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="correo@ejemplo.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Origen</label>
              <select value={form?.origen} onChange={e => handleChange('origen', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="">Seleccionar origen</option>
                {ORIGENES?.map(o => <option key={o?.id} value={o?.id}>{o?.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Servicio de interés</label>
              <input value={form?.servicio_interes} onChange={e => handleChange('servicio_interes', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="¿Qué servicio le interesa?" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Notas</label>
              <textarea value={form?.notas} onChange={e => handleChange('notas', e?.target?.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Notas adicionales..." />
            </div>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="default" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar prospecto'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Nueva Actividad Modal ────────────────────────────────────────────────────
const NuevaActividadModal = ({ contact, contacts, onClose, onSave, companyId, currentUserId }) => {
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const [form, setForm] = useState({
    tipo: 'llamada',
    fecha: today,
    hora: '',
    notas: '',
    estado: 'pendiente',
    contact_id: contact?.id || '',
    responsable: currentUserId || null,
    reminder_minutes: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.fecha) { setError('La fecha es requerida'); return; }
    if (!form?.contact_id) { setError('El contacto es requerido'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        contact_id: form?.contact_id,
        tipo: form?.tipo,
        fecha: form?.fecha,
        hora: form?.hora || null,
        estado: form?.estado,
        notas: form?.notas || null,
        responsable: form?.responsable || null,
        company_id: companyId || null,
        reminder_minutes: form?.reminder_minutes !== null && form?.reminder_minutes !== '' ? parseInt(form?.reminder_minutes, 10) : null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al guardar la actividad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Nueva actividad</h2>
            {contact && <p className="text-sm text-muted-foreground">{contact?.nombre}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {!contact && contacts && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Contacto <span className="text-red-500">*</span></label>
              <select value={form?.contact_id} onChange={e => handleChange('contact_id', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="">Seleccionar contacto</option>
                {contacts?.map(c => <option key={c?.id} value={c?.id}>{c?.nombre}{c?.empresa ? ` — ${c?.empresa}` : ''}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
            <select value={form?.tipo} onChange={e => handleChange('tipo', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {TIPOS_ACTIVIDAD?.map(t => <option key={t?.id} value={t?.id}>{t?.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Fecha <span className="text-red-500">*</span></label>
              <input type="date" value={form?.fecha} onChange={e => handleChange('fecha', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Hora</label>
              <input type="time" value={form?.hora} onChange={e => handleChange('hora', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Recordar antes de la hora</label>
            <select
              value={form?.reminder_minutes === null ? '' : form?.reminder_minutes}
              onChange={e => handleChange('reminder_minutes', e?.target?.value === '' ? null : parseInt(e?.target?.value, 10))}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Sin alerta</option>
              <option value="5">5 min antes</option>
              <option value="10">10 min antes</option>
              <option value="15">15 min antes</option>
              <option value="20">20 min antes</option>
              <option value="30">30 min antes</option>
              <option value="45">45 min antes</option>
              <option value="60">60 min antes</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Estado</label>
            <select value={form?.estado} onChange={e => handleChange('estado', e?.target?.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {ESTADOS_ACTIVIDAD?.map(s => <option key={s?.id} value={s?.id}>{s?.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Notas</label>
            <textarea value={form?.notas} onChange={e => handleChange('notas', e?.target?.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Detalles de la actividad..." />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="default" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar actividad'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Ficha Drawer ─────────────────────────────────────────────────────────────
const FichaDrawer = ({ contact, onClose, onNuevaActividad, activities, loadingActivities, onEnviarWhatsApp, onAgendar }) => {
  if (!contact) return null;
  const etapa = getEtapa(contact?.etapa);
  const origenLabel = ORIGENES?.find(o => o?.id === contact?.origen)?.label || contact?.origen || '—';
  const hasWA = contact?.whatsapp || contact?.telefono;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-card shadow-xl overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{contact?.nombre}</h2>
            <EtapaBadge etapa={contact?.etapa} />
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors"><Icon name="X" size={18} /></button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          {/* Contact info */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Datos del prospecto</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {contact?.empresa && (
                <div className="col-span-2 flex items-center gap-2">
                  <Icon name="Building2" size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{contact?.empresa}</span>
                </div>
              )}
              {contact?.telefono && (
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{contact?.telefono}</span>
                </div>
              )}
              {contact?.whatsapp && (
                <div className="flex items-center gap-2">
                  <Icon name="MessageCircle" size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{contact?.whatsapp}</span>
                </div>
              )}
              {contact?.email && (
                <div className="col-span-2 flex items-center gap-2">
                  <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{contact?.email}</span>
                </div>
              )}
              {contact?.origen && (
                <div className="flex items-center gap-2">
                  <Icon name="Tag" size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{origenLabel}</span>
                </div>
              )}
              {contact?.servicio_interes && (
                <div className="col-span-2 flex items-center gap-2">
                  <Icon name="Wrench" size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{contact?.servicio_interes}</span>
                </div>
              )}
            </div>
            {contact?.notas && (
              <div className="bg-muted/40 rounded-lg p-3 text-sm text-foreground">
                <p className="text-xs text-muted-foreground mb-1 font-medium">Notas</p>
                {contact?.notas}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Creado: {new Date(contact?.created_at)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>

          {/* Quick actions */}
          <div className="flex gap-2 flex-wrap">
            {hasWA ? (
              <button
                onClick={() => onEnviarWhatsApp([contact])}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium transition-colors border border-green-200"
              >
                <Icon name="MessageCircle" size={16} />
                Enviar WhatsApp
              </button>
            ) : (
              <div className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-muted/40 text-muted-foreground text-sm border border-border">
                <Icon name="AlertCircle" size={14} />
                Sin número de WhatsApp
              </div>
            )}
            {onAgendar && (
              <button
                onClick={() => onAgendar(contact)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors border border-primary/20"
              >
                <Icon name="CalendarPlus" size={16} />
                Agendar trabajo
              </button>
            )}
          </div>

          {/* Activities timeline */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Actividades</h3>
              <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={onNuevaActividad}>
                Nueva actividad
              </Button>
            </div>

            {loadingActivities ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activities?.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                <Icon name="Calendar" size={28} className="text-muted-foreground mx-auto mb-2 opacity-40" />
                Sin actividades registradas
              </div>
            ) : (
              <div className="space-y-3">
                {activities?.map((act, idx) => {
                  const tipo = getTipoActividad(act?.tipo);
                  const estadoColor = act?.estado === 'realizado' ? 'text-green-600' : act?.estado === 'cancelado' ? 'text-red-500' : 'text-yellow-600';
                  return (
                    <div key={act?.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={tipo?.icon} size={14} className="text-primary" />
                        </div>
                        {idx < activities?.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-3 flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">{tipo?.label}</span>
                          <span className={`text-xs font-medium ${estadoColor}`}>{ESTADOS_ACTIVIDAD?.find(s => s?.id === act?.estado)?.label || act?.estado}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(act?.fecha)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {act?.hora ? ` · ${act?.hora?.slice(0, 5)}` : ''}
                        </p>
                        {act?.notas && <p className="text-xs text-foreground mt-1 bg-muted/40 rounded p-2">{act?.notas}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Kanban Card ──────────────────────────────────────────────────────────────
const KanbanCard = ({ contact, onVerFicha, onNuevaActividad, onMoverEtapa, onConvertir, onEnviarWhatsApp, onAgendar }) => {
  const [showConvertMenu, setShowConvertMenu] = useState(false);
  const actCount = contact?.activities?.length || 0;
  const lastAct = contact?.activities?.[0];
  const hasWA = contact?.whatsapp || contact?.telefono;

  return (
    <div className="bg-card rounded-lg border border-border p-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{contact?.nombre}</p>
          {contact?.empresa && <p className="text-xs text-muted-foreground truncate">{contact?.empresa}</p>}
        </div>
        <span className="text-xs text-muted-foreground flex-shrink-0">{actCount} act.</span>
      </div>
      {contact?.servicio_interes && (
        <p className="text-xs text-muted-foreground mb-2 truncate">
          <Icon name="Wrench" size={10} className="inline mr-1" />{contact?.servicio_interes}
        </p>
      )}
      {lastAct && (
        <p className="text-xs text-muted-foreground mb-2">
          Última: {new Date(lastAct?.fecha)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
        </p>
      )}
      <select
        value={contact?.etapa}
        onChange={e => onMoverEtapa(contact?.id, e?.target?.value)}
        className="w-full text-xs px-2 py-1 rounded border border-border bg-background mb-2 focus:outline-none focus:ring-1 focus:ring-primary"
        onClick={e => e?.stopPropagation()}
      >
        {ETAPAS?.map(e => <option key={e?.id} value={e?.id}>{e?.label}</option>)}
      </select>
      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => onVerFicha(contact)} className="flex-1 text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors">
          Ficha
        </button>
        <button onClick={() => onNuevaActividad(contact)} className="flex-1 text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors">
          Actividad
        </button>
        {hasWA && (
          <button onClick={() => onEnviarWhatsApp([contact])} title="Enviar WhatsApp" className="text-xs px-2 py-1 rounded bg-green-50 hover:bg-green-100 text-green-700 transition-colors">
            <Icon name="MessageCircle" size={12} />
          </button>
        )}
        {onAgendar && (
          <button onClick={() => onAgendar(contact)} title="Agendar trabajo" className="text-xs px-2 py-1 rounded bg-primary/10 hover:bg-primary/20 text-primary transition-colors">
            <Icon name="CalendarPlus" size={12} />
          </button>
        )}
        <div className="relative">
          <button
            onClick={() => setShowConvertMenu(v => !v)}
            className="text-xs px-2 py-1 rounded bg-primary/10 hover:bg-primary/20 text-primary transition-colors flex items-center gap-1"
          >
            Convertir <Icon name="ChevronDown" size={10} />
          </button>
          {showConvertMenu && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-20 min-w-[160px]">
              <button onClick={() => { onConvertir(contact, 'cliente'); setShowConvertMenu(false); }} className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors flex items-center gap-2">
                <Icon name="UserCheck" size={12} className="text-green-600" /> Convertir a Cliente
              </button>
              <button onClick={() => { onConvertir(contact, 'recepcion'); setShowConvertMenu(false); }} className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors flex items-center gap-2">
                <Icon name="Car" size={12} className="text-blue-600" /> Ir a Recepción
              </button>
              <button onClick={() => { onConvertir(contact, 'cotizacion'); setShowConvertMenu(false); }} className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors flex items-center gap-2">
                <Icon name="FileText" size={12} className="text-orange-600" /> Ir a Cotización
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── List Row ─────────────────────────────────────────────────────────────────
const ListRow = ({ contact, lastActivity, onVerFicha, onNuevaActividad, onMoverEtapa, onConvertir, onEnviarWhatsApp, selected, onToggleSelect, onAgendar }) => {
  const [showConvertMenu, setShowConvertMenu] = useState(false);
  const hasWA = contact?.whatsapp || contact?.telefono;

  return (
    <tr className="hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(contact?.id)}
          className="rounded border-border"
        />
      </td>
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-foreground">{contact?.nombre}</p>
          {contact?.email && <p className="text-xs text-muted-foreground">{contact?.email}</p>}
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{contact?.empresa || '—'}</td>
      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{contact?.telefono || '—'}</td>
      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{contact?.servicio_interes || '—'}</td>
      <td className="px-4 py-3">
        <select
          value={contact?.etapa}
          onChange={e => onMoverEtapa(contact?.id, e?.target?.value)}
          className="text-xs px-2 py-1 rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {ETAPAS?.map(e => <option key={e?.id} value={e?.id}>{e?.label}</option>)}
        </select>
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{lastActivity}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {hasWA && (
            <button onClick={() => onEnviarWhatsApp([contact])} title="Enviar WhatsApp" className="p-1.5 rounded hover:bg-green-50 transition-colors text-green-600">
              <Icon name="MessageCircle" size={15} />
            </button>
          )}
          {onAgendar && (
            <button onClick={() => onAgendar(contact)} title="Agendar trabajo" className="p-1.5 rounded hover:bg-primary/10 transition-colors text-primary">
              <Icon name="CalendarPlus" size={15} />
            </button>
          )}
          <button onClick={() => onVerFicha(contact)} title="Ver ficha" className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="Eye" size={15} />
          </button>
          <button onClick={() => onNuevaActividad(contact)} title="Nueva actividad" className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="Plus" size={15} />
          </button>
          <div className="relative">
            <button onClick={() => setShowConvertMenu(v => !v)} title="Convertir" className="p-1.5 rounded hover:bg-muted transition-colors text-primary">
              <Icon name="ArrowRightCircle" size={15} />
            </button>
            {showConvertMenu && (
              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-20 min-w-[170px]">
                <button onClick={() => { onConvertir(contact, 'cliente'); setShowConvertMenu(false); }} className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors flex items-center gap-2">
                  <Icon name="UserCheck" size={12} className="text-green-600" /> Convertir a Cliente
                </button>
                <button onClick={() => { onConvertir(contact, 'recepcion'); setShowConvertMenu(false); }} className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors flex items-center gap-2">
                  <Icon name="Car" size={12} className="text-blue-600" /> Ir a Recepción
                </button>
                <button onClick={() => { onConvertir(contact, 'cotizacion'); setShowConvertMenu(false); }} className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors flex items-center gap-2">
                  <Icon name="FileText" size={12} className="text-orange-600" /> Ir a Cotización
                </button>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CRMManagement = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [view, setView] = useState('tablero'); // 'tablero' | 'lista' | 'agenda' | 'recordatorios'
  const [contacts, setContacts] = useState([]);
  const [agendaActivities, setAgendaActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showNuevoModal, setShowNuevoModal] = useState(false);
  const [actividadContact, setActividadContact] = useState(null);
  const [showActividadSinContacto, setShowActividadSinContacto] = useState(false);

  // Ficha drawer
  const [fichaContact, setFichaContact] = useState(null);
  const [fichaActivities, setFichaActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // WhatsApp modal
  const [waContacts, setWaContacts] = useState(null);

  // Agendar trabajo modal
  const [agendarModal, setAgendarModal] = useState({ isOpen: false, contact: null, activity: null });

  // Bulk select (lista view)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const companyId = userProfile?.company_id;
  const currentUserId = userProfile?.id;

  // ── Contador de solicitudes nuevas del portal ─────────────────────────────
  const [solicitudesNuevas, setSolicitudesNuevas] = useState(0);

  useEffect(() => {
    if (!companyId) return;
    const fetchNuevas = async () => {
      const { count } = await import('../../lib/supabase')?.then(({ supabase }) =>
        supabase
          ?.from('service_requests')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('company_id', companyId)
          ?.eq('status', 'nueva')
      );
      setSolicitudesNuevas(count || 0);
    };
    fetchNuevas();

    // Suscripción en tiempo real para badge
    let channel;
    import('../../lib/supabase')?.then(({ supabase }) => {
      channel = supabase
        ?.channel(`crm_solicitudes_badge_${companyId}`)
        ?.on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'service_requests',
          filter: `company_id=eq.${companyId}`,
        }, () => { fetchNuevas(); })
        ?.subscribe();
    });
    return () => {
      import('../../lib/supabase')?.then(({ supabase }) => {
        if (channel) supabase?.removeChannel(channel);
      });
    };
  }, [companyId]);

  // ── Notificaciones de actividades del usuario actual ──────────────────────
  const {
    totalHoy: misActividadesHoy,
    totalVencidas: misActividadesVencidas,
    refetch: refetchMisActividades,
  } = useCRMActivities(companyId, currentUserId);

  const filteredContacts = contacts?.filter(c =>
    !searchQuery?.trim() ||
    c?.nombre?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    c?.empresa?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    c?.servicio_interes?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const loadContacts = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const data = await crmService?.getContacts(companyId);
      setContacts(data);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  const loadAgendaActivities = useCallback(async () => {
    if (!companyId) return;
    setIsLoadingAgenda(true);
    try {
      const data = await crmService?.getActivitiesForAgenda(companyId);
      setAgendaActivities(data);
    } catch (err) {
      console.error('Error loading agenda:', err);
    } finally {
      setIsLoadingAgenda(false);
    }
  }, [companyId]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  useEffect(() => {
    if (view === 'agenda' || view === 'recordatorios') {
      loadAgendaActivities();
    }
  }, [view, loadAgendaActivities]);

  const openFicha = async (contact) => {
    setFichaContact(contact);
    setLoadingActivities(true);
    try {
      const acts = await crmService?.getActivities(contact?.id);
      setFichaActivities(acts);
    } catch {
      setFichaActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleSaveContact = async (data) => {
    await crmService?.createContact(data);
    showToast('Prospecto creado correctamente');
    await loadContacts();
  };

  const handleSaveActividad = async (data) => {
    try {
      await crmService?.createActivity(data);
      showToast('Actividad registrada correctamente');
      await loadContacts();
      refetchMisActividades();
      if (fichaContact) {
        const acts = await crmService?.getActivities(fichaContact?.id);
        setFichaActivities(acts);
      }
      if (view === 'agenda' || view === 'recordatorios') {
        await loadAgendaActivities();
      }
    } catch (err) {
      throw new Error(err?.message || 'Error al guardar la actividad');
    }
  };

  const handleUpdateActivityStatus = async (activityId, newStatus) => {
    try {
      await crmService?.updateActivity(activityId, { estado: newStatus });
      setAgendaActivities(prev => prev?.map(a => a?.id === activityId ? { ...a, estado: newStatus } : a));
      refetchMisActividades();
      showToast(newStatus === 'realizado' ? 'Actividad marcada como realizada' : 'Actividad cancelada');
    } catch (err) {
      showToast('Error al actualizar actividad', 'error');
    }
  };

  const handleMoverEtapa = async (contactId, newEtapa) => {
    try {
      await crmService?.updateContact(contactId, { etapa: newEtapa });
      setContacts(prev => prev?.map(c => c?.id === contactId ? { ...c, etapa: newEtapa } : c));
      if (fichaContact?.id === contactId) setFichaContact(prev => ({ ...prev, etapa: newEtapa }));
    } catch (err) {
      showToast('Error al mover etapa', 'error');
    }
  };

  const handleConvertir = async (contact, tipo) => {
    if (tipo === 'cliente') {
      try {
        await crmService?.convertToClient(contact?.id, companyId);
        showToast('Prospecto convertido a cliente');
        await loadContacts();
      } catch (err) {
        showToast(err?.message || 'Error al convertir', 'error');
      }
    } else if (tipo === 'recepcion') {
      navigate('/vehicle-reception', { state: { crmContact: contact } });
    } else if (tipo === 'cotizacion') {
      navigate('/quotation-management', { state: { crmContact: contact } });
    }
  };

  const handleEnviarWhatsApp = (contactsArr, initialMessage = '') => {
    setWaContacts({ contacts: contactsArr, initialMessage });
  };

  const handleEnviarWhatsAppFromActivity = (contact, notas) => {
    if (!contact) return;
    handleEnviarWhatsApp([contact], notas || '');
  };

  const handleAgendar = (contact, activity = null) => {
    setAgendarModal({ isOpen: true, contact: contact || null, activity: activity || null });
  };

  const handleAgendarSuccess = async () => {
    showToast('Operación completada correctamente');
    await loadContacts();
    refetchMisActividades();
    if (view === 'agenda' || view === 'recordatorios') {
      await loadAgendaActivities();
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds?.size === filteredContacts?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts?.map(c => c?.id)));
    }
  };

  const handleBulkWhatsApp = () => {
    const selected = filteredContacts?.filter(c => selectedIds?.has(c?.id));
    if (selected?.length === 0) return;
    handleEnviarWhatsApp(selected);
  };

  const contactsByEtapa = ETAPAS?.reduce((acc, e) => {
    acc[e.id] = filteredContacts?.filter(c => c?.etapa === e?.id);
    return acc;
  }, {});

  const getLastActivity = (contact) => {
    const acts = contact?.activities;
    if (!acts?.length) return '—';
    const last = acts?.[0];
    return new Date(last?.fecha)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const pendingTodayCount = (misActividadesHoy || 0) + (misActividadesVencidas || 0);

  const VIEWS = [
    { id: 'tablero',      label: 'Tablero',      icon: 'LayoutGrid' },
    { id: 'lista',        label: 'Lista',         icon: 'List' },
    { id: 'agenda',       label: 'Agenda',        icon: 'CalendarDays' },
    { id: 'recordatorios',label: 'Recordatorios', icon: 'Bell', badge: pendingTodayCount },
    { id: 'solicitudes',  label: 'Solicitudes',   icon: 'ClipboardList', badge: solicitudesNuevas },
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>

        {/* Header */}
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0">
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="text-xl font-heading font-semibold text-foreground leading-tight">CRM — Prospectos</h1>
                <p className="text-sm text-muted-foreground mt-1">Gestión de prospectos y actividades</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* View toggle */}
              <div className="hidden sm:flex items-center bg-muted rounded-lg p-1 gap-0.5">
                {VIEWS?.map(v => (
                  <button
                    key={v?.id}
                    onClick={() => setView(v?.id)}
                    className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v?.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <Icon name={v?.icon} size={14} />
                    {v?.label}
                    {v?.badge > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {v?.badge > 9 ? '9+' : v?.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={() => setShowNuevoModal(true)}>
                <span className="hidden sm:inline">Nuevo prospecto</span>
                <span className="sm:hidden sr-only">Nuevo</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">

          {/* Mobile view toggle */}
          <div className="flex sm:hidden items-center bg-muted rounded-lg p-1 mb-4 w-full overflow-x-auto gap-0.5">
            {VIEWS?.map(v => (
              <button
                key={v?.id}
                onClick={() => setView(v?.id)}
                className={`relative flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${view === v?.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}
              >
                <Icon name={v?.icon} size={12} />
                {v?.label}
                {v?.badge > 0 && (
                  <span className="ml-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                    {v?.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Agenda View */}
          {view === 'agenda' && (
            <AgendaView
              activities={agendaActivities}
              loading={isLoadingAgenda}
              onNuevaActividad={() => setShowActividadSinContacto(true)}
              onUpdateStatus={handleUpdateActivityStatus}
              onEnviarWhatsApp={handleEnviarWhatsAppFromActivity}
              onAgendar={handleAgendar}
            />
          )}

          {/* Recordatorios View */}
          {view === 'recordatorios' && (
            <RecordatoriosView
              activities={agendaActivities}
              loading={isLoadingAgenda}
              currentUserId={currentUserId}
              onUpdateStatus={handleUpdateActivityStatus}
              onEnviarWhatsApp={handleEnviarWhatsAppFromActivity}
              onAgendar={handleAgendar}
              onAbrirContacto={(contact) => {
                const fullContact = contacts?.find(c => c?.id === contact?.id);
                if (fullContact) {
                  openFicha(fullContact);
                } else {
                  openFicha(contact);
                }
              }}
            />
          )}

          {/* Solicitudes del Portal View */}
          {view === 'solicitudes' && (
            <div className="bg-card rounded-xl border border-border p-6">
              <SolicitudesPortalPanel companyId={companyId} />
            </div>
          )}

          {/* Tablero / Lista views */}
          {(view === 'tablero' || view === 'lista') && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {ETAPAS?.slice(0, 4)?.map(etapa => (
                  <div key={etapa?.id} className="bg-card rounded-lg border border-border p-4">
                    <div className={`inline-flex p-2 rounded-lg ${etapa?.bg} mb-2`}>
                      <Icon name="Users" size={16} className={etapa?.text} />
                    </div>
                    <p className="text-2xl font-bold text-foreground">{contactsByEtapa?.[etapa?.id]?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">{etapa?.label}</p>
                  </div>
                ))}
              </div>

              {/* Search */}
              <div className="mb-5 relative max-w-sm">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar prospecto..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="pl-9 pr-4 py-2 text-sm rounded-lg border border-border bg-card w-full focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : view === 'tablero' ? (
                /* ── Tablero Kanban ── */
                (<div className="overflow-x-auto pb-4">
                  <div className="flex gap-4 min-w-max">
                    {ETAPAS?.map(etapa => (
                      <div key={etapa?.id} className="w-64 flex-shrink-0">
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-t-lg ${etapa?.bg} border ${etapa?.border} border-b-0`}>
                          <div className={`w-2 h-2 rounded-full ${etapa?.color}`} />
                          <span className={`text-xs font-semibold ${etapa?.text}`}>{etapa?.label}</span>
                          <span className={`ml-auto text-xs font-bold ${etapa?.text}`}>{contactsByEtapa?.[etapa?.id]?.length || 0}</span>
                        </div>
                        <div className={`min-h-[200px] p-2 rounded-b-lg border ${etapa?.border} bg-muted/20 space-y-2`}>
                          {contactsByEtapa?.[etapa?.id]?.length === 0 ? (
                            <div className="flex items-center justify-center h-20 text-xs text-muted-foreground">Sin prospectos</div>
                          ) : (
                            contactsByEtapa?.[etapa?.id]?.map(contact => (
                              <KanbanCard
                                key={contact?.id}
                                contact={contact}
                                onVerFicha={openFicha}
                                onNuevaActividad={c => setActividadContact(c)}
                                onMoverEtapa={handleMoverEtapa}
                                onConvertir={handleConvertir}
                                onEnviarWhatsApp={handleEnviarWhatsApp}
                                onAgendar={handleAgendar}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>)
              ) : (
                /* ── Vista Lista ── */
                (<div>
                  <div className="bg-card rounded-xl border border-border overflow-hidden">
                    {filteredContacts?.length === 0 ? (
                      <div className="text-center py-16">
                        <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">No hay prospectos registrados</p>
                        <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={() => setShowNuevoModal(true)}>
                          Nuevo prospecto
                        </Button>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/40">
                              <th className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedIds?.size === filteredContacts?.length && filteredContacts?.length > 0}
                                  onChange={handleSelectAll}
                                  className="rounded border-border"
                                />
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Nombre</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden md:table-cell">Empresa</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden sm:table-cell">Teléfono</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Servicio de interés</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Etapa</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden md:table-cell">Última actividad</th>
                              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filteredContacts?.map(contact => (
                              <ListRow
                                key={contact?.id}
                                contact={contact}
                                lastActivity={getLastActivity(contact)}
                                onVerFicha={openFicha}
                                onNuevaActividad={c => setActividadContact(c)}
                                onMoverEtapa={handleMoverEtapa}
                                onConvertir={handleConvertir}
                                onEnviarWhatsApp={handleEnviarWhatsApp}
                                selected={selectedIds?.has(contact?.id)}
                                onToggleSelect={handleToggleSelect}
                                onAgendar={handleAgendar}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  {/* Bulk WhatsApp bar */}
                  {selectedIds?.size > 0 && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-card border border-border rounded-xl shadow-xl px-5 py-3 flex items-center gap-4">
                      <span className="text-sm font-medium text-foreground">{selectedIds?.size} seleccionado{selectedIds?.size !== 1 ? 's' : ''}</span>
                      <button
                        onClick={handleBulkWhatsApp}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
                      >
                        <Icon name="MessageCircle" size={15} />
                        Enviar WhatsApp a seleccionados
                      </button>
                      <button
                        onClick={() => setSelectedIds(new Set())}
                        className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  )}
                </div>)
              )}
            </>
          )}
        </main>
      </div>
      {/* Modals */}
      {showNuevoModal && (
        <NuevoProspectoModal
          companyId={companyId}
          onClose={() => setShowNuevoModal(false)}
          onSave={handleSaveContact}
        />
      )}
      {actividadContact && (
        <NuevaActividadModal
          contact={actividadContact}
          contacts={contacts}
          companyId={companyId}
          currentUserId={currentUserId}
          onClose={() => setActividadContact(null)}
          onSave={handleSaveActividad}
        />
      )}
      {showActividadSinContacto && (
        <NuevaActividadModal
          contact={null}
          contacts={contacts}
          companyId={companyId}
          currentUserId={currentUserId}
          onClose={() => setShowActividadSinContacto(false)}
          onSave={handleSaveActividad}
        />
      )}
      {/* Ficha Drawer */}
      {fichaContact && (
        <FichaDrawer
          contact={fichaContact}
          activities={fichaActivities}
          loadingActivities={loadingActivities}
          onClose={() => setFichaContact(null)}
          onNuevaActividad={() => setActividadContact(fichaContact)}
          onEnviarWhatsApp={handleEnviarWhatsApp}
          onAgendar={handleAgendar}
        />
      )}
      {/* WhatsApp Modal */}
      {waContacts && (
        <WhatsAppModal
          contacts={waContacts?.contacts}
          initialMessage={waContacts?.initialMessage}
          companyId={companyId}
          onClose={() => setWaContacts(null)}
          onSaveActivity={handleSaveActividad}
        />
      )}
      {/* Agendar Trabajo Modal — solo se monta al abrirse */}
      {agendarModal?.isOpen && agendarModal?.contact && (
        <AgendarTrabajoModal
          isOpen={agendarModal?.isOpen}
          contact={agendarModal?.contact}
          activity={agendarModal?.activity || null}
          onClose={() => setAgendarModal({ isOpen: false, contact: null, activity: null })}
          onSuccess={handleAgendarSuccess}
        />
      )}
      {/* Toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CRMManagement;